import { Controller } from "@hotwired/stimulus";
import { getNotyf } from "../services/toast";

// Controller para el editor de ventas POS
// Maneja la tabla de items, búsqueda de productos y auto-save
export default class extends Controller {
  static targets = [
    "itemsTable",
    "itemsBody",
    "newItemRow",
    "newBarcodeInput",
    "newDescriptionInput",
    "newQuantityInput",
    "autocompleteResults",
    "orderData",
    "pendingBadge",
    "pendingButton",
    "paymentButton",
    "subtotal",
    "total",
    "printTicket",
  ];

  static values = {
    searchUrl: String,
    debounceMs: { type: Number, default: 300 },
  };

  connect() {
    this.searchTimeout = null;
    this.selectedAutocompleteIndex = -1;
    this.notyf = getNotyf();

    // Focus en el campo de código de barras al conectar
    if (this.hasNewBarcodeInputTarget) {
      this.newBarcodeInputTarget.focus();
    }
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  // ==================
  // BÚSQUEDA DE PRODUCTOS
  // ==================

  // Manejar Enter en campo de código de barras (nuevo item)
  handleNewBarcodeKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      const barcode = event.target.value.trim();
      if (barcode) {
        this.searchByBarcode(barcode);
      }
    }
  }

  // Manejar input en campo de descripción (búsqueda parcial)
  handleDescriptionInput(event) {
    const query = event.target.value.trim();

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (query.length < 2) {
      this.hideAutocomplete();
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this.searchByDescription(query);
    }, this.debounceMs);
  }

  // Manejar teclas en campo de descripción
  handleDescriptionKeydown(event) {
    if (!this.hasAutocompleteResultsTarget) return;

    const results =
      this.autocompleteResultsTarget.querySelectorAll(".autocomplete-item");

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.selectedAutocompleteIndex = Math.min(
          this.selectedAutocompleteIndex + 1,
          results.length - 1,
        );
        this.highlightAutocompleteItem(results);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.selectedAutocompleteIndex = Math.max(
          this.selectedAutocompleteIndex - 1,
          0,
        );
        this.highlightAutocompleteItem(results);
        break;
      case "Enter":
        event.preventDefault();
        if (
          this.selectedAutocompleteIndex >= 0 &&
          results[this.selectedAutocompleteIndex]
        ) {
          this.selectAutocompleteItem(results[this.selectedAutocompleteIndex]);
        }
        break;
      case "Escape":
        this.hideAutocomplete();
        break;
    }
  }

  // Búsqueda exacta por código de barras
  async searchByBarcode(barcode) {
    try {
      const response = await fetch(
        `${this.searchUrlValue}?q=${encodeURIComponent(barcode)}&type=barcode`,
        {
          headers: { Accept: "application/json" },
        },
      );

      const data = await response.json();

      if (data.length > 0) {
        this.addItemToOrder(data[0]);
      } else {
        this.showToast("Producto no encontrado", "error");
        this.newBarcodeInputTarget.select();
      }
    } catch (error) {
      console.error("Error buscando producto:", error);
      this.showToast("Error al buscar producto", "error");
    }
  }

  // Búsqueda parcial por descripción
  async searchByDescription(query) {
    try {
      const response = await fetch(
        `${this.searchUrlValue}?q=${encodeURIComponent(query)}&type=description`,
        {
          headers: { Accept: "application/json" },
        },
      );

      const data = await response.json();
      this.showAutocompleteResults(data);
    } catch (error) {
      console.error("Error buscando producto:", error);
    }
  }

  // ==================
  // AUTOCOMPLETE
  // ==================

  showAutocompleteResults(products) {
    if (!this.hasAutocompleteResultsTarget) return;

    if (products.length === 0) {
      this.autocompleteResultsTarget.innerHTML =
        '<div class="autocomplete-empty">No se encontraron productos</div>';
    } else {
      this.autocompleteResultsTarget.innerHTML = products
        .map(
          (product, index) => `
        <div class="autocomplete-item"
             data-inventory-id="${product.id}"
             data-barcode="${this.escapeAttr(product.barcode || "")}"
             data-description="${this.escapeAttr(product.description)}"
             data-price="${product.price}"
             data-action="click->sale-editor#selectAutocompleteItemFromClick">
          <div class="autocomplete-item__description">${this.escapeHtml(product.description)}</div>
          <div class="autocomplete-item__stock">Stock: ${product.available_quantity}</div>
        </div>
      `,
        )
        .join("");
    }

    this.autocompleteResultsTarget.style.display = "block";
    this.selectedAutocompleteIndex = -1;
  }

  hideAutocomplete() {
    if (this.hasAutocompleteResultsTarget) {
      this.autocompleteResultsTarget.style.display = "none";
      this.autocompleteResultsTarget.innerHTML = "";
    }
    this.selectedAutocompleteIndex = -1;
  }

  highlightAutocompleteItem(items) {
    items.forEach((item, index) => {
      item.classList.toggle(
        "autocomplete-item--selected",
        index === this.selectedAutocompleteIndex,
      );
    });
  }

  selectAutocompleteItemFromClick(event) {
    const item = event.currentTarget;
    this.selectAutocompleteItem(item);
  }

  selectAutocompleteItem(item) {
    const inventoryId = item.dataset.inventoryId;
    const barcode = item.dataset.barcode;
    const description = item.dataset.description;
    const price = parseFloat(item.dataset.price);

    this.addItemToOrder({
      id: inventoryId,
      barcode: barcode,
      description: description,
      price: price,
    });

    this.hideAutocomplete();
  }

  // ==================
  // GESTIÓN DE ITEMS
  // ==================

  async addItemToOrder(product) {
    if (!this.hasOrderDataTarget) {
      this.showToast("No hay orden activa", "error");
      return;
    }

    const orderData = this.orderDataTarget;
    const addItemUrl = orderData.dataset.addItemUrl;
    const quantity = this.hasNewQuantityInputTarget
      ? parseInt(this.newQuantityInputTarget.value) || 1
      : 1;

    try {
      const response = await fetch(addItemUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({
          inventory_id: product.id,
          quantity: quantity,
        }),
      });

      if (response.ok) {
        // Turbo Stream manejará la actualización
        const html = await response.text();
        Turbo.renderStreamMessage(html);

        // Limpiar campos y enfocar de nuevo
        this.clearNewItemFields();
        this.enablePaymentButton();
      } else {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("text/vnd.turbo-stream")) {
          const html = await response.text();
          Turbo.renderStreamMessage(html);
        } else {
          try {
            const error = await response.json();
            this.showToast(error.error || "Error al agregar producto", "error");
          } catch {
            this.showToast("Error al agregar producto", "error");
          }
        }
      }
    } catch (error) {
      console.error("Error agregando item:", error);
      this.showToast("Error al agregar producto", "error");
    }
  }

  async updateQuantity(event) {
    const row = event.target.closest("tr");
    const itemId = row.dataset.itemId;
    const newQuantity = parseInt(event.target.value) || 1;

    if (!this.hasOrderDataTarget) return;

    const updateItemUrl = this.orderDataTarget.dataset.updateItemUrl;

    try {
      const response = await fetch(updateItemUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({
          item_id: itemId,
          quantity: newQuantity,
        }),
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
      } else {
        // Handle stock validation errors
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("text/vnd.turbo-stream")) {
          const html = await response.text();
          Turbo.renderStreamMessage(html);
        } else {
          try {
            const error = await response.json();
            this.showToast(
              error.error || "Error al actualizar cantidad",
              "error",
            );
          } catch {
            this.showToast("Error al actualizar cantidad", "error");
          }
        }
      }
    } catch (error) {
      console.error("Error actualizando cantidad:", error);
      this.showToast("Error al actualizar cantidad", "error");
    }
  }

  async updatePrice(event) {
    const row = event.target.closest("tr");
    const itemId = row.dataset.itemId;
    const newPrice = parseFloat(event.target.value);

    if (isNaN(newPrice) || newPrice < 0) {
      this.showToast(
        "El precio debe ser un número válido mayor o igual a 0",
        "error",
      );
      return;
    }

    if (!this.hasOrderDataTarget) return;

    const updateItemUrl = this.orderDataTarget.dataset.updateItemUrl;

    try {
      const response = await fetch(updateItemUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({
          item_id: itemId,
          unit_price: newPrice,
        }),
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
      } else {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("text/vnd.turbo-stream")) {
          const html = await response.text();
          Turbo.renderStreamMessage(html);
        } else {
          try {
            const error = await response.json();
            this.showToast(
              error.error || "Error al actualizar precio",
              "error",
            );
          } catch {
            this.showToast("Error al actualizar precio", "error");
          }
        }
      }
    } catch (error) {
      console.error("Error actualizando precio:", error);
      this.showToast("Error al actualizar precio", "error");
    }
  }

  async removeItem(event) {
    const itemId = event.currentTarget.dataset.itemId;
    if (!this.hasOrderDataTarget) return;

    const removeItemUrl = this.orderDataTarget.dataset.removeItemUrl;

    try {
      const response = await fetch(removeItemUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({ item_id: itemId }),
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
      }
    } catch (error) {
      console.error("Error eliminando item:", error);
    }
  }

  // ==================
  // MODALES
  // ==================

  async openPendingModal() {
    try {
      const response = await fetch("/caja/ordenes/pending_modal", {
        headers: { Accept: "text/html" },
      });

      if (response.ok) {
        const html = await response.text();
        document.getElementById("pos-modal-container").innerHTML = html;

        // Abrir el modal (asumiendo que usa dialog nativo)
        const dialog = document.querySelector("#pos-modal-container dialog");
        if (dialog) {
          dialog.showModal();
        }
      }
    } catch (error) {
      console.error("Error abriendo modal:", error);
    }
  }

  async openPaymentModal() {
    if (!this.hasOrderDataTarget) {
      this.showToast("No hay orden activa", "error");
      return;
    }

    const orderId = this.orderDataTarget.dataset.saleOrderId;

    try {
      const response = await fetch(`/caja/ordenes/${orderId}/payment_modal`, {
        headers: { Accept: "text/html" },
      });

      if (response.ok) {
        const html = await response.text();
        document.getElementById("pos-modal-container").innerHTML = html;

        const dialog = document.querySelector("#pos-modal-container dialog");
        if (dialog) {
          dialog.showModal();
        }
      }
    } catch (error) {
      console.error("Error abriendo modal de pago:", error);
    }
  }

  async openClientModal() {
    try {
      const response = await fetch("/caja/ordenes/client_modal", {
        headers: { Accept: "text/html" },
      });

      if (response.ok) {
        const html = await response.text();
        document.getElementById("pos-modal-container").innerHTML = html;

        const dialog = document.querySelector("#pos-modal-container dialog");
        if (dialog) {
          dialog.showModal();
        }
      }
    } catch (error) {
      console.error("Error abriendo modal de cliente:", error);
    }
  }

  async removeClient(event) {
    const url = event.currentTarget.dataset.url;
    if (!url) return;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
        this.showToast("Cliente removido", "success");
      }
    } catch (error) {
      console.error("Error removiendo cliente:", error);
    }
  }

  async updateDiscount(event) {
    const row = event.target.closest("tr");
    const itemId = row.dataset.itemId;
    const newDiscount = parseFloat(event.target.value) || 0;

    if (!this.hasOrderDataTarget) return;

    const updateItemUrl = this.orderDataTarget.dataset.updateItemUrl;

    try {
      const response = await fetch(updateItemUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({
          item_id: itemId,
          discount_percent: newDiscount,
        }),
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
      }
    } catch (error) {
      console.error("Error actualizando descuento:", error);
    }
  }

  clearOrder() {
    if (!this.hasOrderDataTarget) return;

    if (
      !confirm(
        "¿Estás seguro de que deseas limpiar todos los productos de la orden?",
      )
    )
      return;

    const orderId = this.orderDataTarget.dataset.saleOrderId;
    const clearUrl = `/caja/ordenes/${orderId}/clear_items`;

    fetch(clearUrl, {
      method: "DELETE",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          ?.content,
      },
    })
      .then(async (response) => {
        if (response.ok) {
          const html = await response.text();
          Turbo.renderStreamMessage(html);
          this.showToast("Orden limpiada", "success");
        } else {
          this.showToast("Error al limpiar la orden", "error");
        }
      })
      .catch((error) => {
        console.error("Error limpiando orden:", error);
        this.showToast("Error al limpiar la orden", "error");
      });
  }

  // ==================
  // HELPERS
  // ==================

  clearNewItemFields() {
    if (this.hasNewBarcodeInputTarget) {
      this.newBarcodeInputTarget.value = "";
      this.newBarcodeInputTarget.focus();
    }
    if (this.hasNewDescriptionInputTarget) {
      this.newDescriptionInputTarget.value = "";
    }
    if (this.hasNewQuantityInputTarget) {
      this.newQuantityInputTarget.value = "1";
    }
  }

  enablePaymentButton() {
    if (this.hasPaymentButtonTarget) {
      this.paymentButtonTarget.disabled = false;
    }
  }

  showToast(message, type = "info") {
    if (this.notyf) {
      if (type === "error") {
        this.notyf.error(message);
      } else if (type === "success") {
        this.notyf.success(message);
      } else {
        this.notyf.open({ type: "info", message });
      }
    }
  }

  get debounceMs() {
    return this.debounceMsValue || 300;
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str || "";
    return div.innerHTML;
  }

  escapeAttr(str) {
    return (str || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }
}
